import React, { useState, useRef, useEffect } from 'react';
import { Folder, MoreVertical, Trash2, Pencil, FolderInput, Check, X, Download } from 'lucide-react';

/**
 * FolderCard Component
 * Displays a folder with thumbnail previews of its contents
 * Supports drag/drop for moving items and folders
 *
 * @param {Object} props
 * @param {Object} props.folder - The folder data { id, name, color, thumbnails, itemCount, subfolderCount }
 * @param {function} props.onOpen - Callback when folder is opened (clicked)
 * @param {function} [props.onRename] - Callback to rename the folder (receives folder and newName)
 * @param {function} [props.onDelete] - Callback to delete the folder
 * @param {function} [props.onDownload] - Callback to download folder as ZIP
 * @param {function} [props.onDrop] - Callback when item/folder is dropped onto this folder
 * @param {boolean} [props.draggable] - Whether this folder can be dragged
 * @param {function} [props.onDragStart] - Callback when drag starts
 * @param {function} [props.onDragEnd] - Callback when drag ends
 */
const FolderCard = ({
  folder,
  onOpen,
  onRename,
  onDelete,
  onDownload,
  onDrop,
  draggable = true,
  onDragStart,
  onDragEnd
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);
  const inputRef = useRef(null);

  const handleCardClick = (e) => {
    if (isEditing) return;
    if (onOpen) {
      onOpen(folder);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setEditName(folder.name);
    setIsEditing(true);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDelete) {
      onDelete(folder);
    }
  };

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDownload) {
      onDownload(folder);
    }
  };

  const handleRenameSubmit = (e) => {
    e?.stopPropagation();
    if (editName.trim() && editName.trim() !== folder.name && onRename) {
      onRename(folder, editName.trim());
    }
    setIsEditing(false);
  };

  const handleRenameCancel = (e) => {
    e?.stopPropagation();
    setEditName(folder.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRenameSubmit(e);
    } else if (e.key === 'Escape') {
      handleRenameCancel(e);
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowMenu(false);
    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMenu]);

  // Drag handlers
  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'folder',
      folderId: folder.id,
      folderName: folder.name
    }));
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStart) onDragStart(folder);
  };

  const handleDragEnd = (e) => {
    if (onDragEnd) onDragEnd();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Parse the drag data to check if it's a valid drop
    try {
      const types = e.dataTransfer.types;
      if (types.includes('application/json')) {
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
      }
    } catch {
      // Ignore parsing errors during drag
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDropOnFolder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      // Prevent dropping folder onto itself
      if (data.type === 'folder' && data.folderId === folder.id) {
        return;
      }

      if (onDrop) {
        onDrop(data, folder);
      }
    } catch (error) {
      console.error('Error parsing drop data:', error);
    }
  };

  const thumbnails = folder.thumbnails || [];
  const hasMultipleThumbnails = thumbnails.length > 1;
  const subfolderCount = folder.subfolderCount || 0;

  return (
    <div
      className="card masonry-item"
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      draggable={draggable && !isEditing}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropOnFolder}
      style={{
        padding: 'var(--space-4)',
        cursor: isEditing ? 'default' : 'pointer',
        position: 'relative',
        transition: 'all var(--transition-fast)',
        border: isDragOver
          ? `2px dashed ${folder.color}`
          : '1px solid var(--color-border-light)',
        transform: isHovered && !isDragOver ? 'translateY(-2px)' : 'none',
        boxShadow: isHovered && !isDragOver ? 'var(--shadow-md)' : 'none',
        background: isDragOver ? `${folder.color}10` : undefined,
        opacity: 1
      }}
    >
      {/* Drop indicator overlay */}
      {isDragOver && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `${folder.color}20`,
          borderRadius: 'var(--radius-lg)',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            background: folder.color,
            borderRadius: 'var(--radius-md)',
            color: 'white',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500
          }}>
            <FolderInput size={16} />
            Drop here
          </div>
        </div>
      )}

      {/* Folder thumbnail preview */}
      <div
        style={{
          width: '100%',
          aspectRatio: '1',
          background: `linear-gradient(135deg, ${folder.color}15, ${folder.color}05)`,
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--space-3)',
          overflow: 'hidden',
          position: 'relative',
          border: `2px solid ${folder.color}30`
        }}
      >
        {thumbnails.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: hasMultipleThumbnails ? 'repeat(2, 1fr)' : '1fr',
              gridTemplateRows: hasMultipleThumbnails ? 'repeat(2, 1fr)' : '1fr',
              gap: '2px',
              width: '100%',
              height: '100%',
              padding: '8px'
            }}
          >
            {thumbnails.slice(0, 4).map((thumb, idx) => (
              <div
                key={idx}
                style={{
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  background: 'var(--color-bg-tertiary)'
                }}
              >
                <img
                  src={thumb}
                  alt=""
                  draggable={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ))}
            {/* Fill empty slots with placeholders if less than 4 thumbnails but more than 1 */}
            {hasMultipleThumbnails && thumbnails.length < 4 &&
              Array(4 - thumbnails.length).fill(0).map((_, idx) => (
                <div
                  key={`empty-${idx}`}
                  style={{
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-bg-tertiary)',
                    opacity: 0.5
                  }}
                />
              ))
            }
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Folder
              size={48}
              style={{
                color: folder.color,
                opacity: 0.6
              }}
            />
          </div>
        )}

        {/* Folder icon overlay in corner */}
        <div
          style={{
            position: 'absolute',
            bottom: 'var(--space-2)',
            right: 'var(--space-2)',
            background: folder.color,
            borderRadius: 'var(--radius-md)',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <Folder size={16} color="white" />
        </div>
      </div>

      {/* Folder name - editable */}
      {isEditing ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            marginBottom: 'var(--space-1)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleRenameSubmit}
            style={{
              flex: 1,
              padding: 'var(--space-1) var(--space-2)',
              fontSize: 'var(--font-size-sm)',
              border: '1px solid var(--color-primary)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
              outline: 'none'
            }}
          />
          <button
            onClick={handleRenameSubmit}
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px' }}
          >
            <Check size={14} color="var(--success-600)" />
          </button>
          <button
            onClick={handleRenameCancel}
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px' }}
          >
            <X size={14} color="var(--error-600)" />
          </button>
        </div>
      ) : (
        <h4
          style={{
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--space-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (onRename) {
              setEditName(folder.name);
              setIsEditing(true);
            }
          }}
        >
          {folder.name}
        </h4>
      )}

      {/* Item count and subfolder count */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-tertiary)'
        }}>
          {folder.itemCount || 0} {folder.itemCount === 1 ? 'item' : 'items'}
          {subfolderCount > 0 && ` · ${subfolderCount} ${subfolderCount === 1 ? 'folder' : 'folders'}`}
        </span>

        {/* More menu */}
        {(onDelete || onRename || onDownload) && !isEditing && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={handleMenuClick}
              className="btn btn-ghost btn-sm"
              style={{ padding: 'var(--space-1)' }}
            >
              <MoreVertical size={14} />
            </button>

            {showMenu && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: 'var(--space-1)',
                  background: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 100,
                  minWidth: '120px',
                  overflow: 'hidden'
                }}
              >
                {onDownload && (
                  <button
                    onClick={handleDownloadClick}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-primary)',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Download size={14} />
                    Download as ZIP
                  </button>
                )}
                {onRename && (
                  <button
                    onClick={handleRenameClick}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-primary)',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Pencil size={14} />
                    Rename
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={handleDeleteClick}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--error-600)',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--error-50)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderCard;
